import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-final-form';
import { useParams } from 'react-router';
// api
import api from '@/api';
// styles
import styles from './styles.module.scss';
// images
import FormSection from '@/components/forms/FormSection';
import {
  ForwardArrow,
} from '@/components/landing-page-templates/ConfigLandingPage/LandingPageIcons';
// forms
import { IFormFieldRow, IFormFieldTypes } from '@/types/form';
import validations from '@/validations';
// types
import { IAccountRegistrationEnum } from '@/models/account/types';
import {
  selectRegistrationStep,
  selectSelectedLoginMethod,
} from '@/models/account';
import { ILoginType } from '@/components/landing-page-templates/ConfigLandingPage/types';
//hooks
import {
  selectRegistrationCustomFields,
} from '@/models/event';
//component
import Loader from '@/components/ui/Loader';
import TermsAndConditions from '@/components/landing-page-templates/ConfigLandingPage/DisplayMessageComponents/TermsAndConditions';

const {
  required,
  composeValidators,
  minLength,
  maxLength,
  passwordRules,
  confirmPasswordsMatch,
  email,
  validFirstName,
  validLastName,
} = validations;

const ProfileDetailsForm = ({
  buttonStyle,
  currentUser,
  currentUserEventPermissions,
}) => {
  const dispatch = useDispatch();
  const { eventId } = useParams() as any;
  const registrationCustomFields = useSelector(selectRegistrationCustomFields);

  const selectedLoginType = useSelector(selectSelectedLoginMethod);

  // The user's last login method, if it exists
  const [lastLoginMethod, setLastLoginMethod] = useState();

  // Select registration step to determine if it's a name update and password update or reset password request
  const registrationStep = useSelector(selectRegistrationStep);

  // Only add the password fields if this was a new account created through the Cotter OTP method.
  const { newAccount } = currentUser;
  const emailNotShared = currentUser.email.endsWith('@facebook.example');

  const [loading, setLoading] = useState(false);

  // Get the Account last login method
  // useEffect(() => {
  //   // Get the last login method like "FACEBOOK" so we can direct the user to use that login method
  //   api.account
  //     .checkAccountLoginType(currentUser.email)
  //     .then(loginMethodResponse => {
  //       const loginMethod = ILoginType[loginMethodResponse.data];
  //       setLastLoginMethod(loginMethod);
  //     });
  // }, []);

  // Set section label; if not a new account or collecting email, it's a password reset request
  const sectionLabel: string =
    newAccount ||
      emailNotShared ||
      registrationStep === IAccountRegistrationEnum.CREATE_ACCOUNT
      ? 'Create an Account'
      : 'Reset your Password';

  // Initial form values
  const initialValues = {
    // If default name is set, don't insert default name into the form, requiring them to update it
    firstName: currentUser.firstName.trim() === '' ? registrationCustomFields?.firstName : currentUser.firstName,
    lastName: currentUser.lastName.trim() === '' ? registrationCustomFields?.lastName : currentUser.lastName,

    email: emailNotShared ? '' : currentUser.email,
  };

  // Dynamic form setup
  const profileUpdateFormFields = [] as IFormFieldRow[];

  // Add update first and last name fields if they're blank or default "Event Attendee"
  if (
    registrationStep === IAccountRegistrationEnum.CREATE_ACCOUNT ||
    emailNotShared
  ) {
    profileUpdateFormFields.push({
      fields: [
        {
          name: 'firstName',
          label: '',
          placeholder: 'First Name',
          fieldType: IFormFieldTypes.LANDING_PAGE_INPUT,
          widthRatio: 1,
          validate: composeValidators(required, maxLength(50), validFirstName),
          maxLength: 50,
          tooltip: '',
        },
      ],
    });
    // Add last name field to form
    profileUpdateFormFields.push({
      fields: [
        {
          name: 'lastName',
          label: '',
          placeholder: 'Last Name',
          fieldType: IFormFieldTypes.LANDING_PAGE_INPUT,
          widthRatio: 1,
          validate: composeValidators(required, maxLength(50), validLastName),
          maxLength: 50,
          tooltip: '',
        },
      ],
    });
  }

  // Add email field if the email was not shared (e.g. User did not give permission for facebook to share email)
  if (emailNotShared) {
    profileUpdateFormFields.push({
      fields: [
        {
          name: 'emailInput',
          label: '',
          placeholder: 'Email',
          fieldType: IFormFieldTypes.LANDING_PAGE_INPUT,
          widthRatio: 1,
          validate: composeValidators(required, email),
          maxLength: 150,
          tooltip: '',
        },
      ],
    });
  }

  // Add the password fields if the account registration step is RESET_PASSWORD or if
  // it's a new account; but current only for email & email+OTP logins
  if (
    (lastLoginMethod === ILoginType.EMAIL ||
      lastLoginMethod === ILoginType.EMAIL_OTP ||
      lastLoginMethod === undefined) &&
    (registrationStep === IAccountRegistrationEnum.RESET_PASSWORD ||
      registrationStep === IAccountRegistrationEnum.CREATE_ACCOUNT ||
      newAccount) && 
      (selectedLoginType === ILoginType.EMAIL && newAccount != undefined)
      //Above comparing newAccount with undefined specifically because
      //false has different meaning for forgot password
  ) {
    // Add the password update field
    profileUpdateFormFields.push({
      fields: [
        {
          name: 'password',
          label: '',
          placeholder: 'New Password',
          fieldType: IFormFieldTypes.PASSWORD,
          widthRatio: 1,
          validate: composeValidators(required, minLength(8), passwordRules),
          maxLength: 50,
          tooltip: '',
          styleClass: "landingPagePasswordFullWidth",
          passwordType: 'new',
        },
      ],
    });
    // Add password confirm field
    profileUpdateFormFields.push({
      fields: [
        {
          name: 'confirmPassword',
          label: '',
          placeholder: 'Confirm New Password',
          fieldType: IFormFieldTypes.PASSWORD,
          widthRatio: 1,
          validate: composeValidators(
            required,
            minLength(8),
            passwordRules,
            confirmPasswordsMatch,
          ),
          maxLength: 50,
          tooltip: '',
          styleClass: "landingPagePasswordFullWidth",
        },
      ],
    });
  }

  const register = async () => {
    try {
      const { data } = await api.event.registerOrUpdateEventRole(
        eventId,
        registrationCustomFields,
        true,
      );
      if (data) {
        dispatch({
          type: 'event/saveEventRole',
          payload: {
            eventId,
            eventRole: {
              ...currentUserEventPermissions,
              customField: registrationCustomFields.customField,
            },
          },
        });
        dispatch({
          type: 'event/getEventRole',
          payload: {
            eventId,
          },
        });
      }
    } catch (error: any) {
      const errorMessage = error.response.data.message.replace(/_/g, ' ');
      if (errorMessage.includes('MANDATORY')) {
        dispatch({
          type: 'global/addDangerToast',
          payload: { description: `${errorMessage}` },
        });
      } else {
        dispatch({
          type: 'global/addDangerToast',
          payload: { description: 'Failed to register for the event' },
        });
      }
    }
  }

  // The handler for clicking the button or when Enter key is pressed
  const handleFormSubmit = async ({ firstName, lastName, emailInput, password }) => {
    setLoading(true)

    const profilePayload = {
      firstName: null,
      lastName: null,
      email: null,
      password: null,
    };
    if (firstName) {
      profilePayload.firstName = firstName;
    }
    if (lastName) {
      profilePayload.lastName = lastName;
    }
    if (emailInput) {
      profilePayload.email = emailInput;
    }
    if (password) {
      profilePayload.password = password;
    }

    try {
      const { data } = await api.account.profileUpdateNew(profilePayload);
      if (data) {
        // dispatch({ type: 'account/setAccount' });
        // dispatch({ type: 'account/setUserData', payload: { data } });

        if (registrationStep === IAccountRegistrationEnum.RESET_PASSWORD) {
          dispatch({ type: 'account/setUserData', payload: { data } });
          dispatch({
            type: 'account/setRegistrationStep',
            payload: { registrationStep: IAccountRegistrationEnum.RESET_PASSWORD_SUCCESS },
          });
          return;
        }

        register();
        // dispatch({
        //   type: 'account/setRegistrationStep',
        //   payload: { registrationStep: IAccountRegistrationEnum.COMPLETE },
        // });
      }
    } catch (error: any) {
      // 406 "Not Acceptable" error means the email they want to update to is already registered
      if (error.response.status === 406) {
        dispatch({
          type: 'global/addDangerToast',
          payload: { description: 'This email is already registered.' },
        });
      } else {
        dispatch({
          type: 'global/addDangerToast',
          payload: { description: 'Failed to set profile details.' },
        });
      }
    }
  };

  return (
    <div>
      <div className={styles.sectionHeader}>
        <p className={styles.sectionLabel}>{sectionLabel}</p>
      </div>
      <div className={styles.profileUpdateForm}>
        <Form
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
          render={({ handleSubmit, submitting }) => (
            <form onSubmit={handleSubmit}>
              <FormSection styleClass="landingPageFormSection" formData={profileUpdateFormFields}></FormSection>
              {sectionLabel === 'Reset your Password' &&
                <TermsAndConditions />
              }
              <div className={styles.button}>
                <button
                  type="submit"
                  onSubmit={handleSubmit}
                  onClick={handleSubmit}
                  style={buttonStyle}
                  disabled={loading}
                  className={styles.submitButton}
                >
                  {loading && <Loader />}
                  {/* Forward Arrow SVG */}
                  <ForwardArrow color={buttonStyle.color} />
                  <p className={styles.buttonText}>{loading ? 'Please Wait ...' : 'Continue'}</p>
                </button>
              </div>
            </form>
          )}
        />
      </div>
    </div>
  );
};
export default ProfileDetailsForm;
